import React, { createContext, useContext, useMemo, useState } from 'react';

import { type FTemplateSectionFragment, type FWaypointTemplateFragment } from '@dashboard/generated/wp/platform';
import { UnfoldLessOutlined, UnfoldMoreOutlined } from '@mui/icons-material';
import { Button, Tooltip } from '@mui/material';

type SectionsContextState = {
  sections: FTemplateSectionFragment[];
  collapsedSectionIds: Set<string>;
  setCollapsedSectionIds: (value: Set<string>) => void;
};

const SectionsContext = createContext<SectionsContextState | null>(null);

type SectionsStoreProviderProps = {
  sections: FTemplateSectionFragment[];
  templates: FWaypointTemplateFragment[];
  children: React.ReactNode;
};
export function SectionsStoreProvider({ sections, children }: SectionsStoreProviderProps) {
  const [collapsedSectionIds, setCollapsedSectionIds] = useState<Set<string>>(() => new Set());

  const value = useMemo<SectionsContextState>(
    () => ({ sections, collapsedSectionIds, setCollapsedSectionIds }),
    [sections, collapsedSectionIds]
  );

  return <SectionsContext.Provider value={value}>{children}</SectionsContext.Provider>;
}

function useSectionsStoreContext() {
  const context = useContext(SectionsContext);
  if (context === null) {
    throw new Error('useSectionsStoreContext must be used within a SectionsStoreProvider');
  }
  return context;
}

type TSectionState = {
  expanded: boolean;
  setExpanded: (value: boolean) => void;
};
export function useSectionState(sectionId: string) {
  const { collapsedSectionIds, setCollapsedSectionIds } = useSectionsStoreContext();
  return useMemo<TSectionState>(() => {
    const expanded = !collapsedSectionIds.has(sectionId);
    return {
      expanded,
      setExpanded: (value: boolean) => {
        const newState = new Set<string>(collapsedSectionIds);
        if (value) {
          newState.delete(sectionId);
        } else {
          newState.add(sectionId);
        }
        setCollapsedSectionIds(newState);
      },
    };
  }, [collapsedSectionIds, sectionId, setCollapsedSectionIds]);
}

export function CollapseExpandAllButton() {
  const { sections, collapsedSectionIds, setCollapsedSectionIds } = useSectionsStoreContext();

  if (sections.length === 0) {
    return <span />;
  }

  // All are collapsed
  if (collapsedSectionIds.size === sections.length) {
    return (
      <Tooltip title="Expand all sections">
        <Button
          startIcon={<UnfoldMoreOutlined />}
          size="small"
          onClick={() => {
            setCollapsedSectionIds(new Set());
          }}
          sx={{ color: 'text.secondary' }}
        >
          Expand all
        </Button>
      </Tooltip>
    );
  }

  return (
    <Tooltip title="Collapse all sections">
      <Button
        startIcon={<UnfoldLessOutlined />}
        size="small"
        onClick={() => {
          setCollapsedSectionIds(new Set(sections.map((s) => s.id)));
        }}
        sx={{ color: 'text.secondary' }}
      >
        Collapse all
      </Button>
    </Tooltip>
  );
}
